import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import { useForm } from "react-hook-form";
import { Switch } from "@headlessui/react";
import { useEffect, useState } from "react";
import { BsInfoCircle } from "react-icons/bs";
import { useUtils } from "@/app/hooks/useUtils";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import RBACFlag from "@/app/components/core/RBACFlag";
import AcceptClosingSortes from "./AcceptClosingSortes";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import AddClinicCashComponent from "./AddClinicCashComponent";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useCMSTransactionList } from "@/app/hooks/api_hooks/dayClosing/useCashClosingList";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { useCashSortegsActionAPI } from "@/app/hooks/api_hooks/dayClosing/useCashSortegsActionAPI";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const ClinicCashClosing = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { veticDateFormat, truncateString } = useUtils();
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const { orderclinicList } = routeState.state as IOrderManagementState;
  const [isChecked, setIsChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [accept, setAccept] = useState(false);
  const [shortage_id, setShortage_id] = useState(null);
  const [source, setSource] = useState(null);
  const { execute: fetchClosingList, clinicCashList } = useCMSTransactionList();
  const { execute: actionOnCmsTransaction } = useCashSortegsActionAPI();
  const clinicItemsList = useSelectMenuReducer(orderclinicList, "name", "id");
  // RBAC: ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW — legacy semantics: everyone EXCEPT
  // clinic_ops / clinic_ops_bluecity / clinic_ops_doggyworld. Seed this action for
  // every non-clinic role that should see the central view.
  const isCentralOpps = useFeatureAccess("ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW");

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      clinc_id: mappedClinicId,
    },
  });

  const tableHeader = [
    { item: "Date" },
    { item: "Collection" },
    { item: "Handover" },
    { item: "Shortage" },
    { item: "Recovery" },
    { item: "Opening Balance" },
    { item: "Closing Balance" },
    { item: "CMS Status" },
    { item: "Action Against Shortage" },
    { item: "" },
  ];

  const tableHeader2 = [
    { item: "Date" },
    { item: "Collection" },
    { item: "Handover" },
    { item: "Shortage" },
    { item: "Recovery" },
    { item: "Opening Balance" },
    { item: "Closing Balance" },
    { item: "CMS Status" },
  ];

  const handelChange = () => {
    if (isChecked) {
      setIsChecked(false);
      handleFetchCMSdata(false);
    } else {
      setIsChecked(true);
      handleFetchCMSdata(true);
    }
  };

  const handleFetchCMSdata = (isSorteg: boolean) => {
    setIsTransactionLoading(true);
    fetchClosingList(formHook.watch("clinics"), isSorteg).then(() => {
      setIsTransactionLoading(false);
    });
  };

  useEffect(() => {
    handleFetchCMSdata(false);
  }, []);

  const handelActionOnsortes = (action: string, shortage_request_id: string) => {
    const payload_json = {
      clinic_id: mappedClinicId,
      shortage_request_id: shortage_request_id,
      action: action,
    };
    actionOnCmsTransaction(payload_json).then(() => {
      handleFetchCMSdata(false);
    });
  };

  useEffect(() => {
    if (formHook.watch("clinics")) {
      handleFetchCMSdata(false);
    }
  }, [formHook.watch("clinics")]);

  useEffect(() => {
    if (mappedClinicId) {
      formHook.setValue("clinics", mappedClinicId);
      setIsTransactionLoading(true);
      fetchClosingList(mappedClinicId, false).then(() => {
        setIsTransactionLoading(false);
      });
    }
  }, [mappedClinicId]);

  return (
    <div className="p-1">
      <div className="bg-white shadow-sm flex justify-between p-3 rounded-xl mb-2">
        <div>
          <h2 className="font-semibold text-sm">Cash Management System</h2>
          <p className="text-[#6C727F] font-xs">Includes daily ledger entries and Cash Handover to CMS.</p>
        </div>
        <div className="flex items-center">
          {isCentralOpps && (
            <div className="flex items-center gap-2">
              <p className="font-medium  text-xs">Shortage Only</p>
              <Switch
                checked={isChecked}
                onChange={() => {
                  handelChange();
                }}
                className={clsx(
                  isChecked ? "bg-red-600" : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    isChecked ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                  )}
                />
              </Switch>
            </div>
          )}
          {isCentralOpps && (
            <SearchableMultiSelectMenu
              onSelectItem={(items) => {
                if (items) {
                  const clinicID = items.map((i) => i.id);
                  formHook.setValue("clinics", clinicID);
                }
              }}
              withSelectAll
              showTooltips
              withDropDownSearch
              errorMessages={[{ message: "Required", type: "required" }]}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics", {
                required: true,
              })}
              showTypedErrors={false}
              containerClassName="ml-2"
              placeholder="Select Clinics"
              defaultSelected={clinicItemsList?.filter((c) => formHook.watch("clinics")?.includes(c.id))}
              selectItems={clinicItemsList}
            />
          )}
          {!isCentralOpps && (
            <button
              onClick={() => setOpen(true)}
              className="bg-black font-medium px-4 py-2 rounded-lg text-white text-xs"
            >
              Cash Handover
            </button>
          )}
        </div>
      </div>
      {open && <AddClinicCashComponent open={open} setOpen={setOpen} handleFetchCMSdata={handleFetchCMSdata} />}
      <div className="flex justify-end pb-0"> </div>
      {isTransactionLoading ? (
        <TableSkeleton />
      ) : clinicCashList?.length > 0 ? (
        clinicCashList?.map((item) => {
          return (
            item?.cash_reports?.length > 0 && (
              <div className="bg-white rounded-xl mb-2">
                {clinicCashList?.length > 1 && <p className="text-sm font-semibold p-2">{item?.clinic_name}</p>}
                <div className={`w-full rounded-xl`}>
                  <table className="w-full table-fixed border-separate border-spacing-y-1 bg-[#F5F5F5] pt-1 px-1 rounded-[10px]">
                    <thead className="bg-[#F5F5F5]">
                      <tr>
                        {(isCentralOpps ? tableHeader : tableHeader2).map((key, index, arr) => (
                          <th
                            key={index}
                            scope="col"
                            className={`px-3 py-1 pb-2 w-full text-left text-xs font-medium whitespace-nowrap capitalize tracking-wider
        ${index === 0 ? "rounded-tl-xl" : ""}
        ${index === arr.length - 1 ? "rounded-tr-xl" : ""}`}
                          >
                            {key.item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {item?.cash_reports
                        ?.filter((row) => row.date === new Date().toISOString().split("T")[0])
                        .map((row, index) => (
                          <tr key={`today-${index}`} className="bg-white shadow-sm rounded-xl">
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal rounded-l-xl">
                              <div>
                                <p>{veticDateFormat(row.date)}</p>
                                {row?.shift && (
                                  <button className="bg-[#F1F1F1] text-[#6C727F] text-xs px-4 py-1 mt-1 rounded-md capitalize">
                                    {row?.shift}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.collected_cash}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.handover_amount}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                              <div>
                                <p>{row.shortage}</p>
                                {row.shortage_reason && (
                                  <p
                                    className="text-[#C94A00] text-[10px] truncate max-w-[150px]"
                                    data-tooltip-id="shortage"
                                    data-tooltip-content={row.shortage_reason}
                                  >
                                    *{truncateString(row.shortage_reason, 16)}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.recovery}</td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                              {row.opening_balance}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                              <span
                                className="text-[10px] truncate max-w-[150px] flex gap-1"
                                data-tooltip-id="shortage"
                                data-tooltip-content={
                                  "Closing balance = Opening balance + Collection - Handover - Shortage + Recovery"
                                }
                              >
                                {row.closing_balance} <BsInfoCircle className="w-4 h-4 text-[#C94A00]" />
                              </span>
                            </td>
                            <td
                              className={`px-3 py-4 whitespace-nowrap text-xs font-normal ${!isCentralOpps && "rounded-r-xl"}`}
                            >
                              <button
                                className={`${
                                  row?.handover_status?.toLowerCase() === "verified"
                                    ? "bg-[#F5FFF4] text-[#00873C]"
                                    : "bg-[#FFF9F4] text-[#C94A00]"
                                } rounded-3xl px-4 py-1.5 font-medium`}
                              >
                                {row?.handover_status}
                              </button>
                            </td>
                            {row?.shortage_pending && isCentralOpps ? (
                              <>
                                <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                                  <RBACFlag actionId="CASH_SORTES_ACTION">
                                    <div className="flex gap-3">
                                      <button
                                        className={`text-[#EB000C] rounded-3xl px-4 py-1.5 font-medium border border-[#EB000C]`}
                                        onClick={() => {
                                          setAccept(true);
                                          setShortage_id(row);
                                          setSource("reject");
                                        }}
                                      >
                                        Reject
                                      </button>
                                      <button
                                        className={`bg-black rounded-lg border-transparent px-4 py-1.5 font-semibold text-white`}
                                        onClick={() => {
                                          setAccept(true);
                                          setShortage_id(row);
                                          setSource("accept");
                                        }}
                                      >
                                        Accept
                                      </button>
                                    </div>
                                  </RBACFlag>
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap text-xs font-normal rounded-r-xl"></td>
                              </>
                            ) : (
                              isCentralOpps && (
                                <>
                                  <td className="px-3 py-4 whitespace-nowrap text-xs font-normal"></td>
                                  <td className="px-3 py-4 whitespace-nowrap text-xs font-normal rounded-r-xl"></td>
                                </>
                              )
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {item?.cash_reports?.filter((row) => row.date !== new Date().toISOString().split("T")[0])?.length >
                  0 && (
                  <div className={`w-full overflow-hidden rounded-xl mt-2 border`}>
                    <table className="w-full table-fixed divide-y divide-gray-200">
                      <tbody>
                        {item?.cash_reports
                          ?.filter((row) => row.date !== new Date().toISOString().split("T")[0])
                          .map((row, index) => (
                            <tr
                              key={`other-${index}`}
                              className={`${index !== 0 && "border-t"} ${
                                index % 2 === 0 ? "bg-[#F5F5F5]" : "bg-white"
                              } ${index !== item?.cash_reports?.filter((row) => row.date !== new Date().toISOString().split("T")[0]).length - 1 ? "" : ""}`}
                            >
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                                <div>
                                  <p>{veticDateFormat(row.date)}</p>
                                  {row?.shift && (
                                    <button className="bg-[#F1F1F1] text-[#6C727F] text-xs px-4 py-1 mt-1 rounded-md capitalize">
                                      {row?.shift}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.collected_cash}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.handover_amount}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                                <div>
                                  <p>{row.shortage}</p>
                                  {row.shortage_reason && (
                                    <p
                                      className="text-[#C94A00] text-[10px] truncate max-w-[150px]"
                                      data-tooltip-id="shortage"
                                      data-tooltip-content={row.shortage_reason}
                                    >
                                      *{truncateString(row.shortage_reason, 16)}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">{row.recovery}</td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {row.opening_balance}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {row.closing_balance}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                                <button
                                  className={`${
                                    row?.handover_status?.toLowerCase() === "verified"
                                      ? "bg-[#F5FFF4] text-[#00873C]"
                                      : "bg-[#FFF9F4] text-[#C94A00]"
                                  } rounded-3xl px-4 py-1.5 font-medium capitalize`}
                                >
                                  {row?.handover_status}
                                </button>
                              </td>
                              {row?.shortage_pending && isCentralOpps ? (
                                <>
                                  <td className="px-3 py-4 whitespace-nowrap text-xs font-normal">
                                    <RBACFlag actionId="CASH_SORTES_ACTION">
                                      <div className="flex gap-3">
                                        <button
                                          className={`text-[#EB000C] rounded-3xl px-4 py-1.5 font-medium border border-[#EB000C]`}
                                          onClick={() => {
                                            setAccept(true);
                                            setShortage_id(row);
                                            setSource("reject");
                                          }}
                                        >
                                          Reject
                                        </button>
                                        <button
                                          className={`bg-black rounded-lg border-transparent px-4 py-1.5 font-semibold text-white`}
                                          onClick={() => {
                                            setAccept(true);
                                            setShortage_id(row);
                                            setSource("accept");
                                          }}
                                        >
                                          Accept
                                        </button>
                                      </div>
                                    </RBACFlag>
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-xs font-normal rounded-r-xl"></td>
                                </>
                              ) : (
                                isCentralOpps && (
                                  <>
                                    <td className="px-3 py-4 whitespace-nowrap text-xs font-normal"></td>
                                    <td className="px-3 py-4 whitespace-nowrap text-xs font-normal"></td>
                                  </>
                                )
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          );
        })
      ) : (
        <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Cms transaction found" />
      )}
      <Tooltip id="shortage" place="bottom" />
      <Tooltip id="equation" place="bottom" />
      {accept && (
        <AcceptClosingSortes
          open={accept}
          setOpen={setAccept}
          setSource={setSource}
          source={source}
          shortage_id={shortage_id}
          setShortage_id={setShortage_id}
          handelActionOnsortes={handelActionOnsortes}
        />
      )}
    </div>
  );
};

export default ClinicCashClosing;
